import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, ChevronDown, ChevronRight, Loader2, Save,
  Sparkles, AlertCircle, CheckCircle2, FolderTree,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCompany,
  getFyDocketTemplate,
  draftResolutionsBatch,
  saveResolutionsBatch,
} from "@/lib/api";
import { formatIndianDate } from "@/lib/date";

// Default FY for the docket = LAST COMPLETED financial year, not the
// in-progress one. By Feb (FY still running), users almost always want to
// look at the previous year's compliance docket; using the current FY by
// default surfaces dates in the future and confuses people. URL ?fy= still
// wins so deep-links to a specific year keep working.
const defaultDocketFy = () => {
  const d = new Date();
  const curStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  const prevStart = curStart - 1;
  return `${prevStart}-${String(prevStart + 1).slice(-2)}`;
};

// Convert FY label to a human-readable date range.
// '2024-25' → 'FY 2024-25 · 01 Apr 2024 → 31 Mar 2025'
const fyRange = (fy) => {
  try {
    const [y1, y2] = fy.split("-");
    const startYear = parseInt(y1, 10);
    const endYear = y2.length === 4 ? parseInt(y2, 10) : 2000 + parseInt(y2, 10);
    return `01 Apr ${startYear} → 31 Mar ${endYear}`;
  } catch {
    return "";
  }
};

const fyOptions = () => {
  const cur = new Date();
  const startYear = cur.getMonth() >= 3 ? cur.getFullYear() : cur.getFullYear() - 1;
  const opts = [];
  for (let i = 1; i >= -2; i--) {
    const y = startYear + i;
    opts.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return opts;
};

export default function FyDocket() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  // FY: URL param wins → falls back to current FY. Lets users deep-link to a
  // specific year (e.g. /companies/:id/fy-docket?fy=2024-25) AND survives reload.
  const [fy, setFy] = useState(() => searchParams.get("fy") || defaultDocketFy());
  const [meetings, setMeetings] = useState([]); // [{key,title,meeting_type,date,description,items:[{...,enabled}]}]
  const [expanded, setExpanded] = useState({});
  const [drafting, setDrafting] = useState(false);
  const [draftResults, setDraftResults] = useState(null); // {meetingKey: DraftBatchResponse|{error}}
  const [saving, setSaving] = useState({});  // {meetingKey: bool}
  const [saved, setSaved] = useState({});    // {meetingKey: [Resolution]}

  const applyTemplate = useCallback((data) => {
    const ms = (data.meetings || []).map((m) => ({
      ...m,
      date: m.default_date,
      items: (m.items || []).map((it) => ({ ...it, enabled: !it.optional })),
    }));
    setMeetings(ms);
    // Auto-expand all by default for visibility
    setExpanded(Object.fromEntries(ms.map((m) => [m.key, true])));
  }, []);

  // --- Initial load: company + template ---
  useEffect(() => {
    setLoading(true);
    Promise.all([getCompany(id), getFyDocketTemplate(id, fy)])
      .then(([c, t]) => {
        setCompany(c.data);
        applyTemplate(t.data);
      })
      .catch(() => toast.error("Failed to load FY Docket"))
      .finally(() => setLoading(false));
    // fy is intentionally omitted — FY changes route through reloadPlan() which
    // calls applyTemplate directly. Re-running this effect would needlessly
    // refetch company data and reset draft state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, applyTemplate]);

  // --- Reload plan when FY changes ---
  const reloadPlan = async (nextFy) => {
    try {
      const t = await getFyDocketTemplate(id, nextFy);
      applyTemplate(t.data);
      setDraftResults(null);
      setSaved({});
    } catch {
      toast.error("Failed to load template");
    }
  };

  const handleFyChange = (v) => {
    setFy(v);
    // Persist in URL so reload / back-button keeps the choice.
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("fy", v);
      return p;
    }, { replace: true });
    reloadPlan(v);
  };

  const toggleExpand = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  const updateMeeting = (mKey, patch) =>
    setMeetings((prev) => prev.map((m) => (m.key === mKey ? { ...m, ...patch } : m)));

  const toggleItem = (mKey, idx) =>
    setMeetings((prev) =>
      prev.map((m) =>
        m.key === mKey
          ? {
              ...m,
              items: m.items.map((it, i) => (i === idx ? { ...it, enabled: !it.enabled } : it)),
            }
          : m
      )
    );

  const totalSelected = useMemo(
    () => meetings.reduce((sum, m) => sum + m.items.filter((i) => i.enabled).length, 0),
    [meetings]
  );

  const directorDins = useMemo(
    () => (company?.directors || []).map((d) => d.din || d.name).filter(Boolean),
    [company]
  );

  // --- Draft all meetings (in parallel) ---
  const draftAll = async () => {
    if (!company) return;
    if (directorDins.length === 0) {
      toast.error("Company has no directors. Add at least one before generating.");
      return;
    }
    const meetingsWithItems = meetings.filter((m) => m.items.some((i) => i.enabled));
    if (meetingsWithItems.length === 0) {
      toast.error("Select at least one resolution");
      return;
    }
    setDrafting(true);
    const results = {};
    try {
      const payloads = meetingsWithItems.map((m) => ({
        m,
        payload: {
          company_id: company.id,
          items: m.items
            .filter((i) => i.enabled)
            .slice(0, 5)
            .map((i) => ({
              resolution_type_key: i.resolution_type_key,
              custom_title: i.custom_title || null,
              custom_context: i.custom_context || null,
              is_special_resolution: !!i.is_special_resolution,
              include_shareholder_resolution: !!i.include_shareholder_resolution,
            })),
          signing_director_dins: directorDins.slice(0, 2),
          meeting_date: m.date,
          meeting_time: "11:00 AM",
          meeting_place: company.registered_address || "",
          meeting_type: m.meeting_type || "board",
          include_notice: true,
          include_minutes: true,
          use_style_library: true,
        },
      }));

      const settled = await Promise.allSettled(
        payloads.map(({ payload }) => draftResolutionsBatch(payload))
      );
      payloads.forEach(({ m }, i) => {
        const r = settled[i];
        if (r.status === "fulfilled") {
          results[m.key] = r.value.data;
        } else {
          results[m.key] = {
            error: r.reason?.response?.data?.detail || r.reason?.message || "Draft failed",
          };
        }
      });
      setDraftResults(results);
      const okCount = Object.values(results).filter((r) => !r.error).length;
      const failCount = Object.values(results).filter((r) => r.error).length;
      if (okCount > 0) toast.success(`Drafted ${okCount} meeting${okCount > 1 ? "s" : ""}`);
      if (failCount > 0) toast.error(`${failCount} meeting${failCount > 1 ? "s" : ""} failed to draft`);
    } finally {
      setDrafting(false);
    }
  };

  // --- Save a single meeting ---
  const saveMeeting = async (mKey) => {
    const result = draftResults?.[mKey];
    if (!result || result.error) return;
    setSaving((s) => ({ ...s, [mKey]: true }));
    try {
      const res = await saveResolutionsBatch({
        company_id: result.company_id,
        meeting_date: result.meeting_date,
        meeting_time: result.meeting_time,
        meeting_place: result.meeting_place,
        meeting_type: result.meeting_type,
        signing_director_dins: result.signing_director_dins,
        drafts: result.drafts.map((d) => ({
          resolution_type_key: d.resolution_type_key,
          resolution_title: d.resolution_title,
          resolution_body: d.resolution_body,
          notice_body: d.notice_body,
          minutes_body: d.minutes_body,
          shareholder_resolution_body: d.shareholder_resolution_body,
          is_special_resolution: d.is_special_resolution,
          applicable_sections: d.applicable_sections,
          roc_forms: d.roc_forms,
          statutory_timeline: d.statutory_timeline,
          procedure_followed: d.procedure_followed,
          notice_required: d.notice_required,
          notice_date: d.notice_date,
        })),
      });
      setSaved((s) => ({ ...s, [mKey]: res.data || [] }));
      toast.success(`Saved ${res.data.length} resolution${res.data.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [mKey]: false }));
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Loading FY Docket…</div>;
  if (!company) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Company not found</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="fy-docket-page">
      <button
        onClick={() => navigate(`/companies/${id}`)}
        className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2"
        data-testid="docket-back"
      >
        <ArrowLeft size={14} /> Back to {company.name}
      </button>

      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="label-eyebrow text-[11px] tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-2">
            <FolderTree size={12} /> FY COMPLIANCE DOCKET
          </div>
          <h1 className="font-heading text-3xl tracking-tight font-light">
            Annual compliance plan for {company.name}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            {company.company_type} · {meetings.length} meeting{meetings.length === 1 ? "" : "s"} ·{" "}
            {totalSelected} resolution{totalSelected === 1 ? "" : "s"} selected
          </p>
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">Financial Year</label>
          <select
            value={fy}
            onChange={(e) => handleFyChange(e.target.value)}
            className="border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            data-testid="docket-fy-select"
          >
            {fyOptions().map((f) => (
              <option key={f} value={f}>
                FY {f}
              </option>
            ))}
          </select>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono-plex" data-testid="docket-fy-range">
            {fyRange(fy)}
          </div>
        </div>
      </div>

      {/* Meetings list */}
      <div className="space-y-3 mb-8" data-testid="docket-meetings">
        {meetings.map((m) => {
          const isOpen = expanded[m.key];
          const enabled = m.items.filter((i) => i.enabled).length;
          const dr = draftResults?.[m.key];
          const sv = saved?.[m.key];
          return (
            <div
              key={m.key}
              className="border border-zinc-200 bg-white"
              data-testid={`docket-meeting-${m.key}`}
            >
              <button
                onClick={() => toggleExpand(m.key)}
                className="w-full px-5 py-4 flex items-center gap-3 hover:bg-zinc-50 text-left"
                data-testid={`docket-meeting-toggle-${m.key}`}
              >
                {isOpen ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
                <Calendar size={16} className="text-[#002FA7]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {m.title}
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-300 text-zinc-600">
                      {m.meeting_type}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {formatIndianDate(m.date) || m.date} · {enabled}/{m.items.length} items
                  </div>
                </div>
                {sv && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 size={11} /> Saved · {sv.length}
                  </span>
                )}
                {dr?.error && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                    <AlertCircle size={11} /> Failed
                  </span>
                )}
              </button>

              {isOpen && (
                <div className="border-t border-zinc-100 px-5 py-4 space-y-4">
                  <p className="text-[13px] text-zinc-600">{m.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="label-eyebrow block mb-1.5">Meeting Date</span>
                      <input
                        type="date"
                        value={m.date}
                        onChange={(e) => updateMeeting(m.key, { date: e.target.value })}
                        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        data-testid={`docket-meeting-date-${m.key}`}
                      />
                    </label>
                    <label className="block">
                      <span className="label-eyebrow block mb-1.5">Meeting Type</span>
                      <select
                        value={m.meeting_type}
                        onChange={(e) => updateMeeting(m.key, { meeting_type: e.target.value })}
                        className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        data-testid={`docket-meeting-type-${m.key}`}
                      >
                        <option value="board">Board</option>
                        <option value="agm">AGM</option>
                        <option value="egm">EGM</option>
                      </select>
                    </label>
                  </div>

                  {/* Items */}
                  <div className="border border-zinc-200">
                    {m.items.map((it, idx) => (
                      <label
                        key={`${m.key}-${idx}`}
                        className={`flex items-start gap-3 p-3 border-b border-zinc-100 last:border-0 cursor-pointer ${
                          it.enabled ? "bg-[#002FA7]/5" : "bg-white"
                        }`}
                        data-testid={`docket-item-${m.key}-${idx}`}
                      >
                        <input
                          type="checkbox"
                          checked={it.enabled}
                          onChange={() => toggleItem(m.key, idx)}
                          className="mt-1 accent-[#002FA7]"
                          data-testid={`docket-item-toggle-${m.key}-${idx}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-900">
                            {it.title}
                            {it.optional && (
                              <span className="ml-2 text-[9px] tracking-wider uppercase text-amber-700 border border-amber-300 px-1 py-0.5">
                                Optional
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                            {it.applicable_sections?.[0] && <span>{it.applicable_sections[0]}</span>}
                            {it.roc_forms?.[0] && <span>· {it.roc_forms[0]}</span>}
                            {it.resolution_kind && <span>· {it.resolution_kind.toUpperCase()}</span>}
                          </div>
                          {it.note && (
                            <div className="text-[11px] text-zinc-500 mt-1 italic">{it.note}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Draft preview / save block */}
                  {dr && !dr.error && (
                    <div className="border border-emerald-200 bg-emerald-50/40 p-4">
                      <div className="label-eyebrow text-emerald-700 mb-2">Drafts Ready</div>
                      <ul className="space-y-1.5 mb-3">
                        {dr.drafts.map((d, i) => (
                          <li key={d.draft_id || `${d.resolution_type_key}-${i}`} className="text-[13px] text-zinc-700 flex items-start gap-2">
                            <span className="font-mono-plex text-[11px] text-zinc-500 mt-0.5">{i + 1}.</span>
                            <span>{d.resolution_title}</span>
                          </li>
                        ))}
                      </ul>
                      {sv ? (
                        <div className="text-[12px] text-emerald-700 flex items-center gap-2">
                          <CheckCircle2 size={13} /> Saved as {sv.length} resolution{sv.length > 1 ? "s" : ""} —{" "}
                          {sv[0]?.meeting_id ? (
                            <Link to={`/meetings/${sv[0].meeting_id}`} className="underline">
                              View meeting →
                            </Link>
                          ) : (
                            sv.map((r, i) => (
                              <Link key={r.id} to={`/resolutions/${r.id}`} className="underline mr-1">
                                {r.resolution_number}
                                {i < sv.length - 1 ? "," : ""}
                              </Link>
                            ))
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => saveMeeting(m.key)}
                          disabled={!!saving[m.key]}
                          className="swiss-btn-primary"
                          data-testid={`docket-save-${m.key}`}
                        >
                          {saving[m.key] ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                          {saving[m.key] ? "Saving…" : `Save ${dr.drafts.length} Resolution${dr.drafts.length > 1 ? "s" : ""}`}
                        </button>
                      )}
                    </div>
                  )}
                  {dr?.error && (
                    <div className="border border-rose-200 bg-rose-50/40 p-3 text-[12px] text-rose-700 flex items-start gap-2">
                      <AlertCircle size={13} className="mt-0.5" /> {dr.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom action bar */}
      <div className="sticky bottom-4 bg-white border border-zinc-300 shadow-lg p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-zinc-600">
          {totalSelected > 0
            ? `Ready to draft ${totalSelected} resolution${totalSelected > 1 ? "s" : ""} across ${meetings.filter((m) => m.items.some((i) => i.enabled)).length} meeting${meetings.filter((m) => m.items.some((i) => i.enabled)).length > 1 ? "s" : ""}.`
            : "Select at least one resolution to draft."}
        </div>
        <button
          onClick={draftAll}
          disabled={drafting || totalSelected === 0}
          className="swiss-btn-primary"
          data-testid="docket-draft-all"
        >
          {drafting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {drafting ? "Drafting…" : "Draft All Meetings"}
        </button>
      </div>
    </div>
  );
}
